import React, { useState } from "react";
import { Navbar as BootstrapNavbar, Nav, Container } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FiMenu,
  FiUser,
  FiGlobe,
  FiChevronDown,
  FiMapPin,
  FiCalendar,
  FiGift,
  FiBookOpen,
  FiMail,
  FiUserPlus,
  FiSettings,
  FiLogOut,
  FiExternalLink,
} from "react-icons/fi";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../hooks/useUI";
import { useCurrency } from "../../context/CurrencyContext";
import styles from "./styles.module.scss";
import Button, { Button as AppButton } from "../../components/Button";
import CurrencySelector from "../../components/CurrencySelector";
import navLogo from "../../assets/home-images/horizontal-Logo.svg";
import vectorIcon from "../../assets/home-images/Logo.svg";
import vector from "../../assets/home-images/Vector.svg";
import { Dropdown } from "react-bootstrap";
import { SUPPORTED_CURRENCIES, Currency } from "../../types/currency";

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logoutUser } = useAuth();
  const { isNavbarOpen, toggleNav, closeNav } = useUI();
  const { currencyState, changeCurrency } = useCurrency();
  const [selectedLanguage, setSelectedLanguage] = useState("EN");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);

  // Function to get page name based on current path
  const getPageName = () => {
    const path = location.pathname;
    if (path === "/") return null; // Show logo on home page

    const pageNames: { [key: string]: string } = {
      "/destinations": "DESTINATIONS",
      "/retreat-types": "RETREATS",
      "/offers": "OFFERS",
      "/blog": "BLOGS",
      "/contact": "CONTACT US",
      "/about": "ABOUT US",
      "/login": "LOGIN",
      "/signup": "SIGN UP",
      "/profile": "PROFILE",
      "/my-bookings": "MY BOOKINGS",
      "/bookings": "BOOKINGS",
      "/join-as-partner": "JOIN AS PARTNER",
    };

    // Handle exact matches first
    if (pageNames[path]) {
      return pageNames[path];
    }

    // Handle dynamic routes
    if (path.startsWith("/blog/")) {
      return "BLOG POST";
    }

    // For any other path, extract the first segment and capitalize it
    const segments = path.split("/").filter(Boolean);
    if (segments.length > 0) {
      return segments[0].replace(/-/g, " ").toUpperCase();
    }

    return null;
  };

  const currentPageName = getPageName();

  const handleLogout = () => {
    logoutUser();
    closeNav();
    setShowUserDropdown(false);
    setShowPartnerDropdown(false);
    navigate("/");
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    closeNav();
    setShowUserDropdown(false);
    setShowPartnerDropdown(false);
  };

  const handleLanguageChange = (lang: string) => {
    setSelectedLanguage(lang);
    closeNav();
  };

  return (
    <>
      <BootstrapNavbar expand="lg" className={styles.navbar} fixed="top">
        <Container fluid className={styles.navContainer}>
          <div className={styles.navbarContent}>
            {/* Left side - Menu Button */}
            <div className="col-3">
              <div className={styles.leftSection}>
                <AppButton
                  variant="outline"
                  className={styles.menuButton}
                  onClick={toggleNav}
                >
                  {FiMenu({ size: 26 }) as React.ReactElement}
                </AppButton>
              </div>
            </div>
            <div className="col-6">
              {/* Center - Logo or Page Name */}
              <div className={styles.centerSection}>
                {currentPageName ? (
                  <div className={styles.pageName}>{currentPageName}</div>
                ) : (
                  <BootstrapNavbar.Brand
                    as={Link}
                    to="/"
                    className={styles.logo}
                  >
                    <img
                      src={navLogo}
                      alt="Retrego Logo"
                      className={styles.logoImage}
                    />
                  </BootstrapNavbar.Brand>
                )}
              </div>
            </div>

            {/* Right side - Currency Selector + Auth buttons */}
            <div className="col-3">
              <div className={styles.rightSection}>
                {/* Currency Selector - always visible */}

                {!isAuthenticated ? (
                  <>
                    <Button
                      variant="outline"
                      className={styles.authButton}
                      onClick={() => navigate("/login")}
                    >
                      Login/Sign Up
                    </Button>
                    <AppButton
                      variant="primary"
                      className={styles.loginButton}
                      onClick={() => navigate("/join-as-partner")}
                    >
                      Become a Partner
                    </AppButton>
                  </>
                ) : (
                  <>
                    {user?.role === "user" ? (
                      // User Dropdown Menu
                      <Dropdown 
                        className={styles.userDropdown}
                        show={showUserDropdown}
                        onToggle={(isOpen) => setShowUserDropdown(isOpen)}
                        align="start"
                      >
                        <Dropdown.Toggle
                          variant="outline"
                          className={styles.userMenuButton}
                        >
                          {user?.profilePicUrl ? (
                            <img
                              src={user.profilePicUrl}
                              className={styles.userAvatar}
                              alt="User Avatar"
                            />
                          ) : (
                            FiUser({ size: 16 })
                          )}
                          <span className={styles.userName}>
                            {user?.firstName || user?.name || "User"}
                          </span>
                          {FiChevronDown({ size: 12 })}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className={styles.userDropdownMenu}>
                          <Dropdown.Item onClick={() => {
                            navigate("/profile");
                            setShowUserDropdown(false);
                          }}>
                            {FiUser({ size: 14, className: styles.menuIcon })}
                            My Profile
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => {
                            navigate("/my-bookings");
                            setShowUserDropdown(false);
                          }}>
                            {FiCalendar({ size: 14, className: styles.menuIcon })}
                            My Bookings
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item onClick={handleLogout}>
                            {FiLogOut({ size: 14, className: styles.menuIcon })}
                            Logout
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    ) : (
                      // Partner/Admin Dashboard Link + Dropdown
                      <>
                        <AppButton
                          variant="primary"
                          className={styles.dashboardButton}
                          onClick={() =>
                            navigate(
                              user?.role === "admin"
                                ? "/admin/dashboard"
                                : "/partner/dashboard"
                            )
                          }
                        >
                          {FiExternalLink({ className: styles.linkicon })}
                          {user?.role === "admin"
                            ? "Admin Dashboard"
                            : "Partner Dashboard"}
                        </AppButton>
                        <Dropdown 
                          className={styles.userDropdown}
                          show={showPartnerDropdown}
                          onToggle={(isOpen) => setShowPartnerDropdown(isOpen)}
                          align="start"
                        >
                          <Dropdown.Toggle
                            variant="outline"
                            className={styles.userMenuButton}
                          >
                            {user?.profilePicUrl ? (
                              <img
                                src={user.profilePicUrl}
                                className={styles.userAvatar}
                                alt="User Avatar"
                              />
                            ) : (
                              FiUser({ size: 16 })
                            )}
                            <span className={styles.userName}>
                              {user?.firstName || user?.name || "User"}
                            </span>
                            {FiChevronDown({ size: 12 })}
                          </Dropdown.Toggle>
                          <Dropdown.Menu className={styles.userDropdownMenu}>
                            <Dropdown.Item onClick={() => {
                              navigate("/profile");
                              setShowPartnerDropdown(false);
                            }}>
                              {FiUser({ size: 16, className: styles.menuIcon })}
                              My Profile
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={handleLogout}>
                              {FiLogOut({ size: 16, className: styles.menuIcon })}
                              Logout
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </Container>
      </BootstrapNavbar>

      {/* Fullscreen Sidebar Menu */}
      <div
        className={`${styles.fullscreenSidebar} ${isNavbarOpen ? styles.open : ""
          }`}
      >
        <div className={styles.sidebarOverlay} onClick={closeNav}></div>
        <div className={styles.sidebarContent}>
          <div className={styles.sidebarHeader}>
            <button className={styles.closeButton} onClick={closeNav}>
              ×
            </button>

            <div className={styles.headerSelectors}>
              {/* Language Selector */}
              {/* <div className={styles.sidebarSelectorGroup}>
                {
                  FiGlobe({
                    size: 18,
                    className: styles.sidebarGlobeIcon,
                  }) as React.ReactElement
                }
                <Dropdown className={styles.sidebarLanguageDropdown}>
                  <Dropdown.Toggle
                    variant="link"
                    className={styles.sidebarDropdownToggle}
                  >
                    {selectedLanguage}
                    {
                      FiChevronDown({
                        size: 14,
                        className: styles.sidebarChevronIcon,
                      }) as React.ReactElement
                    }
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleLanguageChange("EN")}>
                      English
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleLanguageChange("AR")}>
                      العربية
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleLanguageChange("FR")}>
                      Français
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div> */}

              {/* Currency Selector */}
              <div className={styles.sidebarSelectorGroup}>
                <CurrencySelector
                  variant="navbar"
                  className={styles.sidebarCurrencySelector}
                  
                />
              </div>
            </div>
          </div>

          <nav className={styles.sidebarNav}>
            {/* HOME - Special structure */}
            <div
              className={`${styles.navItem} ${styles.homeNavItem}`}
              onClick={() => handleNavClick("/")}
            >
              <div className={styles.navItemContent}>
                <div className={styles.homeItemWrapper}>
                  <img src={vector} alt="Home" className={styles.navIcon} />
                  <span className={styles.navText}>HOME</span>
                </div>
              </div>
              <span className={styles.navArrow}>→</span>
            </div>

            {/* DESTINATIONS */}
            <div
              className={styles.navItem}
              onClick={() => handleNavClick("/destinations")}
            >
              <div className={styles.navItemContent}>
                {
                  FiMapPin({
                    size: 24,
                    className: styles.navIcon,
                  }) as React.ReactElement
                }
                <span className={styles.navText}>DESTINATIONS</span>
              </div>
              <span className={styles.navArrow}>→</span>
            </div>

            {/* RETREAT TYPES */}
            <div
              className={styles.navItem}
              onClick={() => handleNavClick("/retreat-types")}
            >
              <div className={styles.navItemContent}>
                {
                  FiCalendar({
                    size: 20,
                    className: styles.navIcon,
                  }) as React.ReactElement
                }
                <span className={styles.navText}>RETREAT TYPES</span>
              </div>
              <span className={styles.navArrow}>→</span>
            </div>

            {/* OFFERS */}
            <div
              className={styles.navItem}
              onClick={() => handleNavClick("/offers")}
            >
              <div className={styles.navItemContent}>
                {
                  FiGift({
                    size: 20,
                    className: styles.navIcon,
                  }) as React.ReactElement
                }
                <span className={styles.navText}>OFFERS</span>
              </div>
              <span className={styles.navArrow}>→</span>
            </div>

            {/* BLOGS */}
            <div
              className={styles.navItem}
              onClick={() => handleNavClick("/blog")}
            >
              <div className={styles.navItemContent}>
                {
                  FiBookOpen({
                    size: 20,
                    className: styles.navIcon,
                  }) as React.ReactElement
                }
                <span className={styles.navText}>BLOGS</span>
              </div>
              <span className={styles.navArrow}>→</span>
            </div>

            {/* CONTACT */}
            <div
              className={styles.navItem}
              onClick={() => handleNavClick("/contact")}
            >
              <div className={styles.navItemContent}>
                {
                  FiMail({
                    size: 20,
                    className: styles.navIcon,
                  }) as React.ReactElement
                }
                <span className={styles.navText}>CONTACT</span>
              </div>
              <span className={styles.navArrow}>→</span>
            </div>

            {(!isAuthenticated ||
              (isAuthenticated &&
                user?.role !== "partner" &&
                user?.role !== "admin")) && (
                <div
                  className={styles.navItem}
                  onClick={() => handleNavClick("/join-as-partner")}
                >
                  <div className={styles.navItemContent}>
                    {
                      FiMail({
                        size: 20,
                        className: styles.navIcon,
                      }) as React.ReactElement
                    }
                    <span className={styles.navText}>JOIN AS A PARTNER</span>
                  </div>
                  <span className={styles.navArrow}>→</span>
                </div>
              )}
            {/* <div
              className={styles.navItem}
              onClick={handleLogout}
            >
              <div className={styles.navItemContent}>
                {
                  FiUser({
                    size: 20,
                    className: styles.navIcon,
                  }) as React.ReactElement
                }
                <span className={styles.navText}>LOG OUT</span>
              </div>
              <span className={styles.navArrow}>→</span>
            </div> */}
            

            {!isAuthenticated ? (
              <>{/* Login/Signup can be added here if needed */}</>
            ) : (
              <>
                {/* PROFILE */}
                <div
                  className={styles.navItem}
                  onClick={() => handleNavClick("/profile")}
                >
                  <div className={styles.navItemContent}>
                    {
                      FiUser({
                        size: 20,
                        className: styles.navIcon,
                      }) as React.ReactElement
                    }
                    <span className={styles.navText}>PROFILE</span>
                  </div>
                  <span className={styles.navArrow}>→</span>
                </div>


                {/* MY BOOKINGS */}
                <div
                  className={styles.navItem}
                  onClick={() => handleNavClick("/my-bookings")}
                >
                  <div className={styles.navItemContent}>
                    {
                      FiCalendar({
                        size: 20,
                        className: styles.navIcon,
                      }) as React.ReactElement
                    }
                    <span className={styles.navText}>MY BOOKINGS</span>
                  </div>
                  <span className={styles.navArrow}>→</span>
                </div>
                <div className={styles.navItem} onClick={handleLogout}>
                  <div className={styles.navItemContent}>
                    {
                      FiLogOut({
                        size: 20,
                        className: styles.navIcon,
                      }) as React.ReactElement
                    }
                    <span className={styles.navText}>LOGOUT</span>
                  </div>
                  <span className={styles.navArrow}>→</span>
                </div>
                {/* JOIN AS PARTNER */}
                {/* <div className={styles.navItem} onClick={() => handleNavClick('/join-as-partner')}>
                  <div className={styles.navItemContent}>
                    {FiUserPlus({ size: 20, className: styles.navIcon }) as React.ReactElement}
                    <span className={styles.navText}>JOIN AS PARTNER</span>
                  </div>
                  <span className={styles.navArrow}>→</span>
                </div> */}
              </>
            )}
          </nav>
          <div className={styles.sidebarFooter}>
            <div className={styles.logoSection}>
              <img
                src={vectorIcon}
                alt="Retrego"
                className={styles.footerIcon}
              />
              {/* <div className={styles.logoText}>RETREGO</div> */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
